/**
 * Budget vs Actual + FY Forecast — inline card for MIS Pack.
 * Fetches both endpoints and renders a compact table with per-budget:
 * Budget · YTD Actual · Variance · FY Forecast · Forecast Variance.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fmtINR } from "@/lib/format";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const fyDefault = () => {
  const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};

export default function BudgetVarianceCard() {
  const [fy, setFy] = useState(fyDefault());
  const [bva, setBva] = useState(null);
  const [fc, setFc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/reports/budget-vs-actual?fy=${fy}`),
        api.get(`/reports/budget-forecast?fy=${fy}`),
      ]);
      setBva(a.data); setFc(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load budget vs actual");
    }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const fcById = React.useMemo(() => {
    const m = {}; (fc?.rows || []).forEach((r) => { m[r.id] = r; }); return m;
  }, [fc]);

  const rows = bva?.rows || [];

  return (
    <Card data-testid="budget-variance-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Budget · Actual · Variance · FY Forecast</CardTitle>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">FY</Label>
            <Input value={fy} onChange={(e) => setFy(e.target.value)}
                     className="h-8 w-24 font-mono text-xs" placeholder="2025-26"
                     data-testid="input-bva-fy" />
          </div>
          <Button size="sm" onClick={load} disabled={busy} data-testid="btn-load-bva">
            {busy ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!bva ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No budgets set for FY {fy}. Configure via <a href="/app/budgets" className="underline">Budgets</a>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full dense text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">Budget · Account</th>
                  <th className="text-right">Budget (FY)</th>
                  <th className="text-right">YTD Actual</th>
                  <th className="text-right">Variance</th>
                  <th className="text-right">FY Forecast</th>
                  <th className="text-right">Forecast Δ</th>
                  <th className="text-right pr-2">Run-Rate/mo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const fcr = fcById[r.id] || {};
                  const vsign = r.variance >= 0 ? "text-emerald-600" : "text-red-600";
                  const fsign = (fcr.projected_variance ?? 0) >= 0 ? "text-emerald-600" : "text-red-600";
                  const Icon = r.variance > 0 ? TrendingUp : r.variance < 0 ? TrendingDown : Minus;
                  return (
                    <tr key={r.id} className="border-b" data-testid={`bva-row-${r.id}`}>
                      <td className="py-2">
                        <div className="font-medium text-xs">{r.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {r.account_code} {r.account_name}
                          {r.cost_centre_name ? ` · ${r.cost_centre_name}` : ""}
                        </div>
                      </td>
                      <td className="text-right tabular-nums">{fmtINR(r.total)}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.actual_total)}</td>
                      <td className={`text-right tabular-nums ${vsign}`}>
                        <Icon className="w-3 h-3 inline mr-1" />
                        {fmtINR(r.variance)} ({r.variance_pct}%)
                      </td>
                      <td className="text-right tabular-nums">{fmtINR(fcr.projected_fy_total)}</td>
                      <td className={`text-right tabular-nums ${fsign}`}>{fmtINR(fcr.projected_variance)}</td>
                      <td className="text-right tabular-nums pr-2 text-xs text-muted-foreground">
                        {fmtINR(fcr.monthly_run_rate)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-semibold bg-blue-50 dark:bg-blue-950/30">
                  <td className="py-2">Total</td>
                  <td className="text-right tabular-nums">{fmtINR(bva.total_budget)}</td>
                  <td className="text-right tabular-nums">{fmtINR(bva.total_actual)}</td>
                  <td className={`text-right tabular-nums ${bva.total_variance >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {fmtINR(bva.total_variance)}
                  </td>
                  <td className="text-right tabular-nums" colSpan={3}>
                    Elapsed {fc?.elapsed_months || 0} of 12 months
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
